import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, BookOpen, Compass, User, Sparkle, Users, HandHeart, MapPin, Shield, Calendar, ScrollText, Leaf, Wrench, Mail, ArrowRight, LifeBuoy } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";

const ICONS = { BookOpen, Compass, User, Sparkle, Users, HandHeart, MapPin, Shield, Calendar, ScrollText, Leaf, Wrench, Mail };

export default function HelpCenter() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const { data: articles = [], isLoading } = useQuery({
    queryKey: ["help-articles", q],
    queryFn: () => api.get(`/help/articles${q ? `?q=${encodeURIComponent(q)}` : ""}`).then((r) => r.data),
  });

  const categories = [...new Set(articles.map((a) => a.category))];

  return (
    <div data-testid="help-center">
      <Seo title="Help Center" description="Guides for booking offerings, memberships, events, the GAUGE daily practice, community groups and your Enhancing Your World account." />
      <div className="bg-[#144F75] text-[#F9F7F3]">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-16">
          <div className="flex items-center gap-2.5 mb-3"><LifeBuoy size={26} className="text-[#D98E73]" /><p className="eyw-label !text-[#D98E73]">Help Center</p></div>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">How to use the ecosystem</h1>
          <p className="text-white/80 mt-3 max-w-2xl">Step-by-step guides for every part of Enhancing Your World — learn how to participate, not just where to click.</p>
          <div className="relative mt-8 max-w-xl">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#144F75]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search guides…" data-testid="help-search"
              className="w-full rounded-full pl-12 pr-4 py-3 text-[#1A242B] outline-none" />
          </div>
          {!user && <p className="text-white/70 text-sm mt-4">Signed-out view: sign in to see guides tailored to your role.</p>}
          <button onClick={() => window.dispatchEvent(new Event("eyw:restart-walkthrough"))} data-testid="restart-tour-btn"
            className="mt-5 inline-flex items-center gap-1.5 text-sm text-white/90 border border-white/25 rounded-full px-4 py-2 hover:bg-white/10 transition-colors">
            <Compass size={15} /> Restart the guided tour
          </button>
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-14">
        {isLoading ? <p className="text-[#4A5D6B]">Loading guides…</p>
          : articles.length === 0 ? <p className="text-[#4A5D6B]" data-testid="help-empty">No guides match your search.</p>
          : categories.map((cat) => (
            <section key={cat} className="mb-12" data-testid={`help-cat-${cat.toLowerCase().replace(/\s+/g, "-")}`}>
              <h2 className="font-serif text-2xl text-[#1A242B] mb-5">{cat}</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {articles.filter((a) => a.category === cat).map((a) => {
                  const Icon = ICONS[a.icon] || BookOpen;
                  return (
                    <Link key={a.slug} to={`/help/${a.slug}`} data-testid={`help-card-${a.slug}`}
                      className="group eyw-card !p-6 flex flex-col hover:-translate-y-1 transition-transform">
                      <span className="grid place-items-center h-11 w-11 rounded-xl bg-[#144F75]/8 text-[#144F75] mb-4"><Icon size={20} /></span>
                      <h3 className="font-serif text-lg text-[#1A242B]">{a.title}</h3>
                      <p className="text-sm text-[#4A5D6B] mt-1.5 flex-1 line-clamp-3">{a.summary}</p>
                      <span className="inline-flex items-center gap-1 text-[#144F75] text-sm font-medium mt-4 group-hover:gap-2 transition-all">Read guide <ArrowRight size={15} /></span>
                    </Link>
                  );
                })}
              </div>
            </section>
          ))}
      </div>
    </div>
  );
}
